/**
 * RejectDialog — reject-with-reason action (EPIC-006 Phase 5, Task 5.3).
 *
 * Requires reason 5–500 chars. Submit button is disabled until valid.
 * Calls POST /admin/seller-applications/{id}/reject.
 */
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet, Button } from "@garden/ui";
import { ApiError } from "@/lib/api-client";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useRejectApplication } from "@/features/admin/hooks/useSellerApplications";

export interface RejectDialogProps {
  open: boolean;
  applicationId: string;
  onClose: () => void;
  onSuccess: () => void;
  onInvalidTransition: () => void;
}

const MIN_REASON = 5;
const MAX_REASON = 500;

export function RejectDialog({
  open,
  applicationId,
  onClose,
  onSuccess,
  onInvalidTransition,
}: RejectDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { mutateAsync, isPending } = useRejectApplication();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const isValid = reason.length >= MIN_REASON && reason.length <= MAX_REASON;

  const handleReject = useCallback(async () => {
    if (!isValid) return;
    setError(null);
    try {
      await mutateAsync({ id: applicationId, reason });
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.problem?.code === "seller_application.invalid_transition") {
          onInvalidTransition();
          return;
        }
      }
      setError(t("error.generic"));
    }
  }, [applicationId, reason, isValid, mutateAsync, onSuccess, onInvalidTransition, t]);

  return (
    <BottomSheet open={open} onClose={onClose} title={t("reject.title")}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="reject-reason"
            className="text-sm font-medium"
          >
            {t("reject.reasonLabel")}
          </label>
          <textarea
            id="reject-reason"
            data-testid="reject-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("reject.reasonPlaceholder")}
            rows={4}
            maxLength={MAX_REASON}
            className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
            aria-describedby="reject-reason-hint"
          />
          <p
            id="reject-reason-hint"
            className="text-xs text-[var(--g-secondary)]"
          >
            {t("reject.reasonHint")} ({reason.length}/{MAX_REASON})
          </p>
        </div>

        {error ? (
          <p className="text-sm text-[var(--g-error)]">{error}</p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isPending}
            className="min-h-[44px]"
          >
            {t("reject.cancel")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            data-testid="reject-confirm-btn"
            onClick={() => void handleReject()}
            disabled={!isValid || isPending}
            className="min-h-[44px] border-[var(--g-error)] text-[var(--g-error)] hover:bg-[var(--g-error)]/10 disabled:opacity-50"
          >
            {t("reject.confirm")}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default RejectDialog;
